import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '@/api/client';
import {
  ArrowLeft, Save, RefreshCw, RotateCcw, Download,
  Loader2, CheckCircle, AlertTriangle, ChevronDown, ChevronRight,
  Sparkles, X, Zap, History, FileText, MessageSquare,
} from 'lucide-react';
import AppFooter from '@/components/layout/AppFooter';

import { apiErrorText } from '../../lib/describeApiError';
/**
 * SkillStudio — admin-only surface for managing the
 * DRAFTING_SKILL_DOCUMENT.
 *
 * Phase 14 — three tabs: Document / Assistant / History.
 * No split screen. Each tab is full width.
 *
 *   Document  — existing markdown editor (Phases 1-13B). Pending-
 *               suggestion banner appears above the editor when
 *               the operator has an unapplied suggestion from the
 *               Assistant tab (Phase 14D).
 *   Assistant — natural-language request → LLM suggestion (no doc
 *               write) → operator reviews / edits / applies via
 *               the deterministic /admin/skill/apply endpoint
 *               (Phase 14B).
 *   History   — session-only list of additions / modifications
 *               applied during this admin session (in-memory; no
 *               persistence; v1).
 *
 * Human oversight mandatory throughout: no LLM output reaches the
 * skill_documents row without an explicit operator click on Apply.
 */

type TabKey = 'document' | 'assistant' | 'history';

interface PendingSuggestion {
  section_anchor:       string | null;
  section_heading:      string | null;
  content:              string;
  is_new_section:       boolean;
  insert_after:         string | null;
  why:                  string;
  mode:                 'add' | 'modify' | 'find';
  intent:               'add' | 'modify' | 'find' | 'question';
  // Find / question mode payload — the LLM puts the matching
  // anchor + excerpt in these fields rather than reusing
  // section_anchor / content. Pulling them through here so the
  // result panel doesn't render an empty 'Found: —' card.
  found_anchor:         string | null;
  found_excerpt:        string;
}

interface HistoryEntry {
  timestamp:        string;
  action:           'added' | 'modified';
  section_anchor:   string;
  heading:          string | null;
  content:          string;
  chars_added:      number;
  is_new_section:   boolean;
}

export default function SkillStudio({ onBack }: { onBack: () => void }) {
  const [doc, setDoc] = useState<api.SkillDocResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const [reindexing, setReindexing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [syncResult, setSyncResult] = useState<api.SkillSyncPromptsResponse | null>(null);
  const [confirmSync, setConfirmSync] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const [sectionMapOpen, setSectionMapOpen] = useState(true);
  const [usageOpen, setUsageOpen] = useState(false);

  // ── Phase 14 — tabbed layout state ────────────────────────────
  const [activeTab, setActiveTab] = useState<TabKey>('document');
  const [pendingSuggestion, setPendingSuggestion] =
    useState<PendingSuggestion | null>(null);
  const [sessionHistory, setSessionHistory] = useState<HistoryEntry[]>([]);
  // Phase 14D — when an apply lands, the Document tab scrolls the
  // textarea to this line + flashes the highlighted region for
  // ~3s. Cleared after the highlight fades.
  const [highlightLine, setHighlightLine] = useState<number | null>(null);

  // unsaved-changes indicator drives the dot on the Document tab.
  const docDirty = useMemo(
    () => draft !== (doc?.content ?? ''), [draft, doc?.content],
  );

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await api.getSkillDocument();
      setDoc(data);
      setDraft(data.content ?? '');
    } catch (e: any) {
      setError(apiErrorText(e, 'Failed to load skill document'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const showFlash = (msg: string, ms = 4000) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), ms);
  };

  const handleToggleEnabled = async () => {
    if (!doc) return;
    const next = !doc.skill_guidance_enabled;
    setTogglingEnabled(true);
    setError(null);
    try {
      await api.setSkillEnabled(next);
      setDoc({ ...doc, skill_guidance_enabled: next });
      showFlash(next ? 'Skill guidance enabled.' : 'Skill guidance disabled.');
    } catch (e: any) {
      setError(apiErrorText(e, 'Failed to toggle skill guidance'));
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await api.putSkillDocument(draft);
      setDoc(data);
      setDraft(data.content ?? '');
      showFlash('Saved — document indexed status reset. Click Re-index to rebuild the search index.');
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e?.message || 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    setError(null);
    try {
      const result = await api.reindexSkillDocument();
      if (result.status === 'ok') {
        showFlash(`Re-indexed — ${result.node_count ?? 0} nodes in the tree.`);
        await reload();
      } else {
        setError(result.message || 'Re-index failed');
      }
    } catch (e: any) {
      setError(apiErrorText(e, 'Re-index failed'));
    } finally {
      setReindexing(false);
    }
  };

  const runSyncPrompts = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    try {
      const result = await api.syncSkillPrompts();
      setSyncResult(result);
      showFlash(
        `${result.synced.length} prompt(s) restored to their skill-aware seed.`,
      );
    } catch (e: any) {
      setError(apiErrorText(e, 'Sync failed'));
    } finally {
      setSyncing(false);
      setConfirmSync(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      const data = await api.resetSkillDocument();
      setDoc(data);
      setDraft(data.content ?? '');
      showFlash('Reset to the seed version from the repo.');
    } catch (e: any) {
      setError(apiErrorText(e, 'Reset failed'));
    } finally {
      setResetting(false);
      setConfirmReset(false);
    }
  };

  /**
   * Phase 14C — apply a suggestion. Hits the integrity-gated
   * /admin/skill/apply endpoint; on success: records the apply
   * in sessionHistory, clears pendingSuggestion, switches to the
   * Document tab, reloads the doc. Phase 14D will add the
   * auto-scroll + highlight to the inserted section.
   */
  const applySuggestion = async (sugg: PendingSuggestion) => {
    setError(null);
    try {
      const result = await api.postSkillApply({
        insert_after_anchor:  sugg.insert_after,
        content:              sugg.content,
        is_new_section:       sugg.is_new_section,
        new_section_heading:  sugg.section_heading,
      });
      if (!result.ok) {
        setError('Apply returned ok=false; check server logs.');
        return;
      }
      // Record in session history.
      setSessionHistory((prev) => [
        {
          timestamp:      new Date().toISOString(),
          action:         sugg.is_new_section ? 'added' : 'modified',
          section_anchor: result.applied_anchor,
          heading:        sugg.section_heading,
          content:        sugg.content,
          chars_added:    result.chars_added,
          is_new_section: sugg.is_new_section,
        },
        ...prev,
      ]);
      setPendingSuggestion(null);
      setActiveTab('document');
      // Reload so the textarea reflects the new content.
      await reload();
      // Phase 14D — auto-scroll + highlight to the applied line.
      // The Document tab effect picks this up + scrolls / selects
      // the line range so the operator sees the new content
      // immediately.
      if (result.applied_line) {
        setHighlightLine(result.applied_line);
      }
      showFlash(
        `Applied to ${result.applied_anchor} (+${result.chars_added} chars). ` +
        `Click Re-index to refresh the PageIndex tree.`,
      );
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error_code === 'integrity_check_failed') {
        const codes = (detail.issues || [])
          .map((i: any) => `${i.code}: ${i.message}`)
          .join('; ');
        setError(`Integrity check failed — ${codes}`);
      } else {
        setError(typeof detail === 'string' ? detail : (e?.message || 'Apply failed'));
      }
    }
  };

  const statusChip = () => {
    if (!doc || !doc.exists) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-red-50 text-red-700 text-xs font-medium border border-red-200">
          <AlertTriangle size={12} /> Not loaded
        </span>
      );
    }
    if (doc.indexed && doc.indexed_is_stale) {
      return (
        <span
          title="The document has been edited since the last index. Re-index to refresh."
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200"
        >
          <AlertTriangle size={12} /> Index stale — re-index
        </span>
      );
    }
    if (doc.indexed) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-green-50 text-green-700 text-xs font-medium border border-green-200">
          <CheckCircle size={12} /> Indexed — {doc.pageindex_node_count} nodes
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
        <AlertTriangle size={12} /> Loaded — not indexed
      </span>
    );
  };

  const pill = (ok: boolean, label: string) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs border ${
        ok
          ? 'bg-green-50 text-green-700 border-green-200'
          : 'bg-zinc-50 text-zinc-500 border-zinc-200'
      }`}
    >
      {ok ? <CheckCircle size={11} /> : <X size={11} />}
      {label}
    </span>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-zinc-500">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading skill document…
      </div>
    );
  }

  // ── Tab nav + tab content rendered inside <main> ──────────────
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-testid="skill-back-button"
            onClick={onBack}
            className="text-zinc-500 hover:text-zinc-900 transition-colors p-1"
            title="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
              <Sparkles size={16} className="text-[#002FA7]" />
              Skill Studio
            </h1>
            <p className="text-xs text-zinc-500">
              Manage the DRAFTING_SKILL_DOCUMENT used for Stage 2/3/4 guidance
            </p>
          </div>
        </div>
      </header>

      {/* ── Phase 14 — tab nav ─────────────────────────────────── */}
      <nav
        data-testid="skill-tabs"
        className="border-b border-zinc-200 px-6 flex items-center gap-1"
      >
        <TabButton
          tab="document"
          active={activeTab === 'document'}
          icon={<FileText size={14} />}
          label="Document"
          dot={docDirty}
          onClick={() => setActiveTab('document')}
        />
        <TabButton
          tab="assistant"
          active={activeTab === 'assistant'}
          icon={<MessageSquare size={14} />}
          label="Assistant"
          dot={!!pendingSuggestion}
          onClick={() => setActiveTab('assistant')}
        />
        <TabButton
          tab="history"
          active={activeTab === 'history'}
          icon={<History size={14} />}
          label={`History${sessionHistory.length > 0 ? ` (${sessionHistory.length})` : ''}`}
          onClick={() => setActiveTab('history')}
        />
      </nav>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {flash && (
          <div
            data-testid="skill-flash"
            className="bg-green-50 border border-green-200 text-green-800 text-sm px-3 py-2 rounded-sm flex items-center gap-2"
          >
            <CheckCircle size={14} /> {flash}
          </div>
        )}
        {error && (
          <div
            data-testid="skill-error"
            className="bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded-sm flex items-center gap-2"
          >
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {activeTab === 'document' && (
          <DocumentTab
            doc={doc}
            draft={draft}
            setDraft={setDraft}
            saving={saving}
            reindexing={reindexing}
            syncing={syncing}
            resetting={resetting}
            togglingEnabled={togglingEnabled}
            sectionMapOpen={sectionMapOpen}
            setSectionMapOpen={setSectionMapOpen}
            usageOpen={usageOpen}
            setUsageOpen={setUsageOpen}
            syncResult={syncResult}
            statusChip={statusChip}
            pill={pill}
            handleToggleEnabled={handleToggleEnabled}
            handleSave={handleSave}
            handleReindex={handleReindex}
            handleConfirmSync={() => setConfirmSync(true)}
            handleConfirmReset={() => setConfirmReset(true)}
            pendingSuggestion={pendingSuggestion}
            onApplyPending={() => pendingSuggestion && applySuggestion(pendingSuggestion)}
            onViewPending={() => setActiveTab('assistant')}
            onDiscardPending={() => setPendingSuggestion(null)}
            highlightLine={highlightLine}
            onHighlightCleared={() => setHighlightLine(null)}
          />
        )}
        {activeTab === 'assistant' && (
          <AssistantTab
            pendingSuggestion={pendingSuggestion}
            setPendingSuggestion={setPendingSuggestion}
            onApply={applySuggestion}
            onError={(msg) => setError(msg)}
          />
        )}
        {activeTab === 'history' && (
          <HistoryTab entries={sessionHistory} />
        )}
      </main>

      {confirmSync && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm border border-zinc-200 max-w-md w-full p-5 shadow-lg">
            <h2 className="text-sm font-medium text-zinc-900 mb-2">Sync skill-aware prompts?</h2>
            <p className="text-xs text-zinc-600 mb-4">
              This restores every prompt that declares <code>skill_guidance</code> in
              its optional placeholders to the seed (v2) content shipped with this
              build. Admin edits to those prompt templates will be overwritten.
              Prompts that already match the seed are skipped.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmSync(false)}
                className="px-3 py-1.5 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                data-testid="skill-sync-confirm-btn"
                onClick={runSyncPrompts}
                disabled={syncing}
                className="px-3 py-1.5 text-sm bg-[#002FA7] text-white rounded-sm hover:bg-[#001F70] disabled:opacity-50 flex items-center gap-2"
              >
                {syncing && <Loader2 size={12} className="animate-spin" />}
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmReset && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm border border-zinc-200 max-w-md w-full p-5 shadow-lg">
            <h2 className="text-sm font-medium text-zinc-900 mb-2">Reset to seed?</h2>
            <p className="text-xs text-zinc-600 mb-4">
              This discards your edits and re-imports the DRAFTING_SKILL_DOCUMENT
              from the version shipped in this repo. The PageIndex tree will be cleared —
              run Re-index afterwards to rebuild it.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmReset(false)}
                className="px-3 py-1.5 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                data-testid="skill-reset-confirm-btn"
                onClick={handleReset}
                disabled={resetting}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              >
                {resetting && <Loader2 size={12} className="animate-spin" />}
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
      <AppFooter />
    </div>
  );
}


// ─── Tab nav button ────────────────────────────────────────────

function TabButton({
  tab, active, icon, label, dot, onClick,
}: {
  tab: TabKey;
  active: boolean;
  icon: React.ReactNode;
  label: string;
  dot?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={`skill-tab-${tab}`}
      data-active={active}
      onClick={onClick}
      className={[
        'relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors',
        active
          ? 'text-zinc-900 border-b-2 border-[#002FA7] -mb-[1px]'
          : 'text-zinc-500 hover:text-zinc-900',
      ].join(' ')}
    >
      {icon}
      <span>{label}</span>
      {dot && (
        <span
          className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[#002FA7]"
          aria-label="unsaved or pending"
          data-testid={`skill-tab-${tab}-dot`}
        />
      )}
    </button>
  );
}


// ─── Document tab ──────────────────────────────────────────────

function DocumentTab(props: any): JSX.Element {
  const {
    doc, draft, setDraft, saving, reindexing, syncing, resetting,
    togglingEnabled, sectionMapOpen, setSectionMapOpen, usageOpen,
    setUsageOpen, syncResult, statusChip, pill, handleToggleEnabled,
    handleSave, handleReindex, handleConfirmSync, handleConfirmReset,
    pendingSuggestion, onApplyPending, onViewPending, onDiscardPending,
    highlightLine, onHighlightCleared,
  } = props;
  // Phase 14D — auto-scroll + highlight after apply. When the
  // parent sets highlightLine to a 1-indexed line number, locate
  // the byte range of that line in the textarea, set selection +
  // scroll into view; clear the highlight after 3s so the
  // operator sees the flash but can keep typing afterwards.
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  React.useEffect(() => {
    if (highlightLine == null) return;
    const el = textareaRef.current;
    if (!el) return;
    const lines = (draft || '').split('\n');
    const idx = Math.max(0, Math.min(lines.length - 1, highlightLine - 1));
    let offset = 0;
    for (let i = 0; i < idx; i++) {
      offset += lines[i].length + 1;
    }
    const lineLen = (lines[idx] || '').length;
    el.focus({ preventScroll: true });
    try {
      el.setSelectionRange(offset, offset + lineLen);
    } catch {
      // setSelectionRange can throw on detached textareas; ignore.
    }
    // Heuristic scroll: scrollTop ≈ (line-index / total-lines) *
    // scrollHeight. Slight bias upward so the flashed line lands a
    // few rows below the top of the visible area, not at the very
    // edge.
    const ratio = idx / Math.max(1, lines.length);
    el.scrollTop = Math.max(0, ratio * el.scrollHeight - 60);
    const t = window.setTimeout(() => {
      // Clear selection + parent-side flag.
      try {
        el.setSelectionRange(offset, offset);
      } catch { /* noop */ }
      onHighlightCleared?.();
    }, 3000);
    return () => window.clearTimeout(t);
  }, [highlightLine, draft, onHighlightCleared]);
  return (
    <>
      {/* Phase 14D — pending-suggestion banner. Renders only when
          the operator has an unapplied Assistant suggestion. */}
      {pendingSuggestion && (
        <PendingSuggestionBanner
          suggestion={pendingSuggestion}
          onApply={onApplyPending}
          onView={onViewPending}
          onDiscard={onDiscardPending}
        />
      )}

      <section
        data-testid="skill-status-bar"
        className="border border-zinc-200 rounded-sm p-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="skill-enabled-toggle"
              checked={!!doc?.skill_guidance_enabled}
              onChange={handleToggleEnabled}
              disabled={togglingEnabled}
              className="h-4 w-4"
            />
            <span className="text-sm font-medium text-zinc-900">Skill guidance</span>
          </label>
          <span
            className={`text-xs font-medium ${
              doc?.skill_guidance_enabled ? 'text-green-700' : 'text-zinc-500'
            }`}
          >
            {doc?.skill_guidance_enabled ? 'Active' : 'Inactive'}
          </span>
          {togglingEnabled && <Loader2 size={12} className="animate-spin text-zinc-400" />}
        </div>
        <div className="flex items-center gap-3">
          {statusChip()}
        </div>
      </section>

      <section data-testid="skill-section-map" className="border border-zinc-200 rounded-sm">
        <button
          onClick={() => setSectionMapOpen(!sectionMapOpen)}
          className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-zinc-900 hover:bg-zinc-50"
        >
          <span className="flex items-center gap-2">
            {sectionMapOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Sections detected
          </span>
          <span className="text-xs text-zinc-500">
            {doc?.word_count ?? 0} words
          </span>
        </button>
        {sectionMapOpen && (
          <div className="px-4 pb-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {pill(!!doc?.sections_detected.part1, 'Part 1 — General')}
              {pill(!!doc?.sections_detected.T1_SOFTWARE, 'T1 — Software')}
              {pill(!!doc?.sections_detected.T2_MEDTECH, 'T2 — MedTech')}
              {pill(!!doc?.sections_detected.T3_MECHANICAL, 'T3 — Mechanical')}
              {pill(!!doc?.sections_detected.T4_CHEMISTRY, 'T4 — Chemistry')}
              {pill(!!doc?.sections_detected.T5_ELECTRONICS, 'T5 — Electronics')}
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
              <button
                data-testid="skill-reindex-btn"
                onClick={handleReindex}
                disabled={reindexing || !doc?.exists}
                className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {reindexing
                  ? <Loader2 size={14} className="animate-spin" />
                  : <RefreshCw size={14} />}
                {reindexing ? 'Re-indexing…' : 'Re-index document'}
              </button>
              <button
                data-testid="skill-sync-btn"
                onClick={handleConfirmSync}
                disabled={syncing || !doc?.skill_guidance_enabled}
                title={
                  doc?.skill_guidance_enabled
                    ? 'Restore skill-aware prompts to their seed content'
                    : 'Enable skill guidance first'
                }
                className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {syncing
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Download size={14} />}
                Sync skill-aware prompts
              </button>
            </div>
            {syncResult && (
              <div
                data-testid="skill-sync-result"
                className="mt-2 text-xs border border-zinc-200 rounded-sm p-3 bg-zinc-50"
              >
                <div className="font-medium mb-1">Sync result</div>
                <div><span className="text-green-700">Synced ({syncResult.synced.length}):</span> {syncResult.synced.join(', ') || '—'}</div>
                <div><span className="text-zinc-500">Already up-to-date ({syncResult.skipped.length}):</span> {syncResult.skipped.join(', ') || '—'}</div>
                {syncResult.not_in_seed.length > 0 && (
                  <div><span className="text-amber-700">No seed content ({syncResult.not_in_seed.length}):</span> {syncResult.not_in_seed.join(', ')}</div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section data-testid="skill-editor" className="border border-zinc-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-zinc-900">Skill document</span>
          {doc?.version_hash && (
            <span className="text-xs text-zinc-500 font-mono">
              v{doc.version_hash}
              {doc.indexed_at ? ` · indexed ${new Date(doc.indexed_at).toLocaleString()}` : ''}
            </span>
          )}
        </div>
        <textarea
          data-testid="skill-editor-textarea"
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={28}
          spellCheck={false}
          className={[
            'w-full bg-white border rounded-sm px-3 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y transition-colors duration-300',
            // Phase 14D — green border tint while the post-apply
            // highlight is active; fades back via the
            // transition-colors duration when the timeout clears.
            highlightLine != null
              ? 'border-emerald-400 bg-emerald-50/30'
              : 'border-zinc-300',
          ].join(' ')}
        />
        <div className="mt-3 flex gap-2 items-center">
          <button
            data-testid="skill-save-btn"
            onClick={handleSave}
            disabled={saving || draft === (doc?.content ?? '')}
            className="bg-[#002FA7] text-white hover:bg-[#001F70] rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            data-testid="skill-reset-btn"
            onClick={handleConfirmReset}
            disabled={resetting}
            className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            title="Restore from repo seed"
          >
            <RotateCcw size={14} />
            Reset to seed
          </button>
          {draft !== (doc?.content ?? '') && (
            <span className="text-xs text-amber-600 ml-1">Unsaved changes</span>
          )}
        </div>
      </section>

      <section data-testid="skill-usage-info" className="border border-zinc-200 rounded-sm">
        <button
          onClick={() => setUsageOpen(!usageOpen)}
          className="w-full px-4 py-3 flex items-center gap-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50"
        >
          {usageOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Usage info
        </button>
        {usageOpen && (
          <div className="px-4 pb-4 text-xs text-zinc-500">
            For per-matter visibility, run a matter through Stage 2/3/4 and
            check its admin diagnostic export — the <code>skill_guidance</code>{' '}
            block reports the active bucket, guidance char count, and how many
            LLM calls went out with an empty placeholder.
          </div>
        )}
      </section>
    </>
  );
}


// ─── Pending suggestion banner (Phase 14D) ─────────────────────

function PendingSuggestionBanner({
  suggestion, onApply, onView, onDiscard,
}: {
  suggestion: PendingSuggestion;
  onApply: () => void;
  onView: () => void;
  onDiscard: () => void;
}): JSX.Element {
  const heading = suggestion.section_heading
    || (suggestion.section_anchor ? `Anchor ${suggestion.section_anchor}` : 'Pending suggestion');
  return (
    <div
      data-testid="skill-pending-banner"
      className="bg-blue-50 border border-blue-200 text-blue-900 rounded-sm px-3 py-2 flex items-center gap-3"
    >
      <Zap size={16} className="text-blue-600 shrink-0" />
      <div className="flex-1 text-sm">
        <span className="font-medium">Pending suggestion: </span>
        <span>{heading}</span>
      </div>
      <button
        type="button"
        data-testid="skill-pending-view"
        onClick={onView}
        className="text-xs px-2 py-1 border border-blue-300 rounded-sm hover:bg-blue-100"
      >
        View in Assistant
      </button>
      <button
        type="button"
        data-testid="skill-pending-apply"
        onClick={onApply}
        className="text-xs px-2 py-1 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
      >
        Apply now
      </button>
      <button
        type="button"
        data-testid="skill-pending-discard"
        onClick={onDiscard}
        className="text-xs px-2 py-1 border border-blue-300 rounded-sm hover:bg-blue-100"
      >
        Discard
      </button>
    </div>
  );
}


// ─── Assistant tab ─────────────────────────────────────────────

function AssistantTab({
  pendingSuggestion, setPendingSuggestion, onApply, onError,
}: {
  pendingSuggestion: PendingSuggestion | null;
  setPendingSuggestion: (s: PendingSuggestion | null) => void;
  onApply: (s: PendingSuggestion) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const [request, setRequest] = useState('');
  const [mode, setMode] = useState<'add' | 'modify' | 'find'>('add');
  const [asking, setAsking] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);

  // Local editable suggestion fields (operator can override the
  // LLM's placement before applying).
  const [editAnchor, setEditAnchor] = useState('');
  const [editHeading, setEditHeading] = useState('');
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    // Hydrate local edit state when a new suggestion arrives.
    if (pendingSuggestion) {
      setEditAnchor(pendingSuggestion.insert_after || '');
      setEditHeading(pendingSuggestion.section_heading || '');
      setEditContent(pendingSuggestion.content);
    }
  }, [pendingSuggestion]);

  const ask = async () => {
    if (!request.trim()) return;
    setAsking(true);
    try {
      const result = await api.postSkillAssist({
        user_request: request.trim(),
        mode,
      });
      if (!result.ok) {
        onError(
          `Assistant returned non-JSON. Try rephrasing the request. ` +
          `(error_code=${result.error_code})`,
        );
        return;
      }
      const sugg = result.suggestion!;
      setPendingSuggestion({
        section_anchor:   sugg.section_anchor ?? null,
        section_heading:  sugg.section_heading ?? null,
        content:          sugg.content ?? '',
        is_new_section:   !!sugg.is_new_section,
        insert_after:     sugg.insert_after ?? null,
        why:              sugg.why ?? '',
        mode,
        intent:           sugg.intent,
        found_anchor:     sugg.found_anchor ?? null,
        found_excerpt:    sugg.found_excerpt ?? '',
      });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      onError(
        typeof detail === 'string'
          ? detail
          : (detail?.message || e?.message || 'Assistant call failed'),
      );
    } finally {
      setAsking(false);
    }
  };

  const apply = () => {
    if (!pendingSuggestion) return;
    onApply({
      ...pendingSuggestion,
      insert_after:    editAnchor || pendingSuggestion.insert_after,
      section_heading: editHeading || pendingSuggestion.section_heading,
      content:         editContent,
    });
  };

  return (
    <div className="space-y-4" data-testid="skill-assistant-tab">
      <p className="text-sm text-zinc-600">
        Describe what you want to add or change. The assistant will
        suggest content — you review and edit before anything applies.
      </p>

      <section className="border border-zinc-200 rounded-sm p-4 space-y-3">
        <textarea
          data-testid="skill-assistant-input"
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          rows={4}
          placeholder='e.g. "Add EPO Nov 2024 AI technical character rule" or "find the antecedent basis guidance"'
          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
        />

        <div className="flex items-center gap-4 text-xs">
          <span className="text-zinc-500">Mode:</span>
          {(['add', 'modify', 'find'] as const).map((m) => (
            <label key={m} className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="skill-assist-mode"
                data-testid={`skill-assistant-mode-${m}`}
                value={m}
                checked={mode === m}
                onChange={() => setMode(m)}
              />
              {m === 'add' && 'Add new content'}
              {m === 'modify' && 'Find & modify existing'}
              {m === 'find' && 'Just find — no edit'}
            </label>
          ))}
        </div>

        <button
          type="button"
          data-testid="skill-assistant-ask"
          onClick={ask}
          disabled={asking || !request.trim()}
          className="bg-[#002FA7] text-white hover:bg-[#001F70] rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {asking ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {asking ? 'Asking…' : 'Ask Assistant'}
        </button>
      </section>

      {pendingSuggestion && (
        <section
          data-testid="skill-assistant-suggestion"
          className="border border-blue-200 bg-blue-50/30 rounded-sm p-4 space-y-3"
        >
          {pendingSuggestion.intent === 'find'
            || pendingSuggestion.intent === 'question' ? (
            <>
              <div className="text-sm">
                <span className="font-medium">
                  {pendingSuggestion.intent === 'question'
                    ? 'This looks like a question. The skill document says:'
                    : `Found: ${pendingSuggestion.found_anchor
                        || pendingSuggestion.section_anchor
                        || '—'}`}
                </span>
              </div>
              <pre
                data-testid="skill-assistant-find-excerpt"
                className="text-xs font-mono whitespace-pre-wrap bg-white border border-zinc-200 rounded-sm p-3 max-h-64 overflow-y-auto"
              >
                {pendingSuggestion.found_excerpt
                  || pendingSuggestion.content
                  || '(The assistant did not return an excerpt for this query — try rephrasing.)'}
              </pre>
              <div className="text-xs text-zinc-600">
                {pendingSuggestion.intent === 'question'
                  ? 'Switch mode to Add or Modify to base an edit on this.'
                  : 'Use the Document tab to navigate to this section.'}
              </div>
            </>
          ) : (
            <>
              <div className="text-sm">
                <div className="font-medium">I'll {pendingSuggestion.is_new_section ? 'add' : 'modify'} this in:</div>
                <div className="text-zinc-700">
                  {pendingSuggestion.section_anchor
                    || pendingSuggestion.section_heading
                    || `(after ${pendingSuggestion.insert_after || 'end of doc'})`}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Why: {pendingSuggestion.why}
                </div>
              </div>
              <label className="block text-xs">
                <span className="text-zinc-500">Place after anchor:</span>
                <input
                  type="text"
                  data-testid="skill-assistant-anchor"
                  value={editAnchor}
                  onChange={(e) => setEditAnchor(e.target.value)}
                  placeholder={pendingSuggestion.insert_after || 'e.g. G2b'}
                  className="mt-1 w-full bg-white border border-zinc-300 rounded-sm px-2 py-1 font-mono"
                />
              </label>
              {pendingSuggestion.is_new_section && (
                <label className="block text-xs">
                  <span className="text-zinc-500">Section heading:</span>
                  <input
                    type="text"
                    data-testid="skill-assistant-heading"
                    value={editHeading}
                    onChange={(e) => setEditHeading(e.target.value)}
                    placeholder="### G2c. EPO AI character"
                    className="mt-1 w-full bg-white border border-zinc-300 rounded-sm px-2 py-1 font-mono"
                  />
                </label>
              )}
              <label className="block text-xs">
                <span className="text-zinc-500">Content to add:</span>
                <textarea
                  data-testid="skill-assistant-content"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={10}
                  className="mt-1 w-full bg-white border border-zinc-300 rounded-sm px-2 py-1 font-mono leading-relaxed"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="skill-assistant-apply"
                  onClick={apply}
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm px-3 py-1.5 text-sm font-medium"
                >
                  Apply to Document
                </button>
                <button
                  type="button"
                  data-testid="skill-assistant-discard"
                  onClick={() => setPendingSuggestion(null)}
                  className="border border-zinc-300 rounded-sm px-3 py-1.5 text-sm hover:bg-zinc-50"
                >
                  Discard
                </button>
              </div>
            </>
          )}
        </section>
      )}

      <section className="border border-zinc-200 rounded-sm">
        <button
          type="button"
          onClick={() => setTagsOpen(!tagsOpen)}
          className="w-full px-4 py-3 flex items-center gap-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50"
        >
          {tagsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Tag reference
        </button>
        {tagsOpen && (
          <div className="px-4 pb-4 text-xs text-zinc-600 space-y-1 font-mono">
            <div>[UNIVERSAL]  — applies to every jurisdiction</div>
            <div>[EPO/UPC]    — EPO + UPC only</div>
            <div>[USPTO]      — US only</div>
            <div>[GOOD]: ...  — correct pattern example</div>
            <div>[BAD]:  ...  — incorrect pattern example</div>
          </div>
        )}
      </section>
    </div>
  );
}


// ─── History tab ───────────────────────────────────────────────

function HistoryTab({ entries }: { entries: HistoryEntry[] }): JSX.Element {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  if (entries.length === 0) {
    return (
      <div
        data-testid="skill-history-empty"
        className="text-sm text-zinc-500 border border-zinc-200 rounded-sm p-6 text-center"
      >
        Nothing applied this session yet. Use the Assistant tab to suggest
        content; applied changes appear here.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="skill-history-tab">
      <p className="text-xs text-zinc-500">
        Session-only — clears on page reload. To undo, edit the Document tab
        directly.
      </p>
      {entries.map((e, idx) => (
        <div
          key={idx}
          data-testid={`skill-history-entry-${idx}`}
          className="border border-zinc-200 rounded-sm p-3"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle size={14} className="text-emerald-600" />
              <span className="font-medium text-zinc-900">
                {e.action === 'added' ? 'Added' : 'Modified'} {e.section_anchor}
                {e.heading && ` — ${e.heading.replace(/^#+\s*[A-Z][\w\-]+\.\s*/, '')}`}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setExpanded((p) => ({ ...p, [idx]: !p[idx] }))}
              className="text-xs text-zinc-500 hover:text-zinc-900"
            >
              {expanded[idx] ? 'Hide content' : 'Show what was added ▼'}
            </button>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {new Date(e.timestamp).toLocaleTimeString()} · {e.chars_added} chars
            added · {e.is_new_section ? 'new section' : 'inline edit'}
          </div>
          {expanded[idx] && (
            <pre className="mt-2 text-xs font-mono whitespace-pre-wrap bg-zinc-50 border border-zinc-200 rounded-sm p-3 max-h-80 overflow-y-auto">
              {e.content}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}
